import { useCallback, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  PiCaretRight,
  PiEnvelopeSimple,
  PiGear,
  PiLock,
  PiSparkle,
  PiTrash,
} from 'react-icons/pi'
import { useQuery } from '@tanstack/react-query'

import useApiService from 'services/api'
import { cn } from 'components/UI/cn'
import { Toggle } from 'components/FbUI/Toggle'
import { StatusBadge } from 'components/FbUI/StatusBadge'
import { Alert } from 'components/FbUI/Alert'
import FbButton from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { PillTabs, TabPanel } from 'components/FbUI/PillTabs'
import { resolveAgentIcon } from 'components/RecommendationsCard/agentIcons'
import { FBPageInnerTabsContainer } from 'components/FbUI/Page/styles'
import type {
  RecommendationAgent,
  UserRecommendationNote,
} from 'models/companies'
import type { EmailIntegration } from 'models/emailIntegration'
import { useAuth } from 'context/authentication/useAuth'

// Agents whose sub-features depend on full-inbox access. Kept in sync with
// the backend AGENT_REGISTRY (server/recommendations/pipeline/registry.py):
// - `contact_suggestion` owns the `contact_suggestion` signal type
//   (its create-flavor needs full-inbox to see cold-inbound senders).
// - `update_deal` owns both `email_stage_change` and `create_deal`; the
//   `create_deal` half requires full-inbox ingestion.
const FULL_INBOX_DEPENDENT_AGENT_IDS = new Set([
  'contact_suggestion',
  'update_deal',
])
const EMAIL_INTEGRATIONS_QUERY_KEY = ['ai-settings', 'email-integrations']

// Recommendation-sensitivity levels the backend accepts (signal_filter_level).
// The fourth value, `turn_off`, disables the recommendation pipeline entirely
// (server/recommendations/usecases/generate_action_summary.py) and is driven
// by the AI Features master toggle on the Overview tab — so this slider only
// spans the three filtering strengths.
const SENSITIVITY_LEVELS = [
  {
    value: 'conservative',
    label: 'Conservative',
    description: 'Only the highest-confidence, clearly actionable items.',
  },
  {
    value: 'normal',
    label: 'Normal',
    description:
      'A balanced mix of high-confidence and moderately-confident signals.',
  },
  {
    value: 'aggressive',
    label: 'Aggressive',
    description: 'A wide net — more signals, lower certainty, nothing missed.',
  },
] as const

// The value written when the AI Features master toggle is switched off.
const TURN_OFF = 'turn_off'
// Level restored when the master toggle is switched back on and no prior
// non-off level is known.
const DEFAULT_ON_LEVEL = 'normal'

// Company context shares the 2000-char ceiling of Company.llm_context
// (server/product/models.py). Per-user notes live in the Personalization tab.
const TEXT_MAX_LENGTH = 2000
const COMPANY_CONTEXT_PLACEHOLDER = `Example: We sell plant-based proteins to K-12 districts and universities. Minimum order is 500 cases. Our primary distributors are Sysco and US Foods. Sales cycle is typically 3-6 months for new accounts.`

// Per-user recommendation notes ("memories", FIR-7479). Categories and limits
// mirror server/recommendations/models.py (UserRecommendationNote): the 10-note
// cap spans all categories and bounds the synthesis prompt; 1000 chars each.
const MAX_NOTES = 10
const MAX_NOTE_LENGTH = 1000

const TAB_ID_PREFIX = 'ai-settings'

type AITab =
  | 'overview'
  | 'agents'
  | 'personalization'
  | 'sensitivity'
  | 'permissions'

const TABS: ReadonlyArray<{ key: AITab; title: string }> = [
  { key: 'overview', title: 'Overview' },
  { key: 'agents', title: 'My Agents' },
  { key: 'personalization', title: 'Personalization' },
  { key: 'sensitivity', title: 'Recommendation Sensitivity' },
  { key: 'permissions', title: 'Permissions' },
]

// Single neutral tile behind every agent icon. Kept uniform (not per-agent
// colors) so the green On/Off badges and toggles carry the status signal.
const AGENT_ICON_TILE = 'bg-neutral-100 text-fg-secondary'

// Brand-colored keyboard focus ring for custom clickable cards/pills, so
// focus-visible shows Basil green instead of the UA default blue outline.
const FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-active focus-visible:ring-offset-1'

export function AISettingsPage() {
  const api = useApiService()
  const { user, isAdmin } = useAuth()
  // Per-user recommendation settings (agent toggles, sensitivity) — FIR-7479.
  // Company context (llm_context) stays company-level and is fetched from
  // company-details for the Company Context section on the Personalization tab.
  const {
    data: recSettings,
    isLoading: isLoadingRec,
    refetch: refetchRec,
  } = api.useGetRecommendationSettings()
  const {
    data: companyData,
    isLoading: isLoadingCompany,
    refetch: refetchCompany,
  } = api.useGetCompanyDetails()

  const {
    data: emailIntegrationsData,
    isLoading: isLoadingIntegrations,
    refetch: refetchIntegrations,
  } = useQuery({
    queryKey: EMAIL_INTEGRATIONS_QUERY_KEY,
    queryFn: ({ signal }) =>
      api.emailIntegration.getEmailIntegrationList({ limit: 100 }, signal),
  })

  // Company details power the Company Context section. The company-details GET
  // is IsAuthenticated (not admin-gated), so everyone can read llm_context —
  // admins edit it, non-admins see it read-only — hence we block on its load
  // for all users so the value is populated before the form mounts.
  if (
    isLoadingRec ||
    !recSettings ||
    isLoadingIntegrations ||
    isLoadingCompany ||
    !companyData
  ) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    )
  }

  // Agents are defined by the backend AGENT_REGISTRY and exposed on the
  // per-user settings payload. If the server is older and doesn't include
  // them, fall back to an empty list — settings still renders, the agent
  // section just stays empty until the deploy completes.
  const agents = recSettings.recommendation_agents ?? []
  // Only the requesting user's own integrations are toggleable here —
  // the list endpoint returns shared teammate integrations too, but the
  // backend PATCH guard 403s anyone but the owner, so hide them from the
  // UI rather than render rows the user cannot interact with.
  const myIntegrations: EmailIntegration[] = (
    emailIntegrationsData?.results ?? []
  ).filter((row) => row.user === user?.id)

  return (
    <AISettingsForm
      key={user?.id}
      initialFilterLevel={recSettings.signal_filter_level ?? DEFAULT_ON_LEVEL}
      agents={agents}
      initialEnabledSignalTypes={
        recSettings.enabled_signal_types ??
        agents.flatMap((a) => a.signal_types)
      }
      isAdmin={isAdmin}
      initialCompanyContext={companyData?.llm_context ?? ''}
      emailIntegrations={myIntegrations}
      onSaveFilterLevel={async (filterLevel) => {
        await api.updateRecommendationSettings({
          signal_filter_level: filterLevel,
        })
        await refetchRec()
      }}
      onSaveEnabledSignalTypes={async (types) => {
        await api.updateRecommendationSettings({ enabled_signal_types: types })
        await refetchRec()
      }}
      onSaveCompanyContext={async (companyContext) => {
        await api.updateCompanyDetails({ llmContext: companyContext })
        await refetchCompany()
      }}
      onSaveInboxAccess={async (integrationId, inboxAccess) => {
        await api.emailIntegration.updateEmailIntegration(integrationId, {
          inbox_access: inboxAccess,
        })
        await refetchIntegrations()
      }}
    />
  )
}

/**
 * Owns the editable AI-settings state and renders the tabbed form.
 *
 * The AI Features master switch (FIR-7884) is derived from `signal_filter_level`
 * rather than a field of its own: `aiEnabled === filterLevel !== 'turn_off'`.
 * Switching it off writes `turn_off` (killing the recommendation pipeline);
 * switching it on restores the last real sensitivity level (`lastActiveLevelRef`,
 * falling back to `normal`). No separate API — it reuses `onSaveFilterLevel`.
 */
function AISettingsForm({
  initialFilterLevel,
  agents,
  initialEnabledSignalTypes,
  isAdmin,
  initialCompanyContext,
  emailIntegrations,
  onSaveFilterLevel,
  onSaveEnabledSignalTypes,
  onSaveCompanyContext,
  onSaveInboxAccess,
}: {
  initialFilterLevel: string
  agents: RecommendationAgent[]
  initialEnabledSignalTypes: string[]
  isAdmin: boolean
  initialCompanyContext: string
  emailIntegrations: EmailIntegration[]
  onSaveFilterLevel: (filterLevel: string) => Promise<void>
  onSaveEnabledSignalTypes: (types: string[]) => Promise<void>
  onSaveCompanyContext: (companyContext: string) => Promise<void>
  onSaveInboxAccess: (
    integrationId: number,
    inboxAccess: EmailIntegration['inbox_access']
  ) => Promise<void>
}) {
  const [activeTab, setActiveTab] = useState<AITab>('overview')
  const [filterLevel, setFilterLevel] = useState(initialFilterLevel)
  const [enabledTypes, setEnabledTypes] = useState<string[]>(
    initialEnabledSignalTypes
  )
  const [savingField, setSavingField] = useState<string | null>(null)

  // The sensitivity level to restore when the master toggle is switched back
  // on. Seeded from the initial level (or a sensible default when the feature
  // loads already turned off), and kept current as the user picks levels.
  const lastActiveLevelRef = useRef(
    initialFilterLevel !== TURN_OFF ? initialFilterLevel : DEFAULT_ON_LEVEL
  )

  // AI Features are "on" whenever the pipeline isn't short-circuited by
  // turn_off — the master toggle and the whole feature hang off this.
  const aiEnabled = filterLevel !== TURN_OFF

  const hasFullInboxIntegration = useMemo(
    () =>
      emailIntegrations.some(
        (integ) => integ.inbox_access === 'full_inbox_access'
      ),
    [emailIntegrations]
  )

  // An agent is "enabled" when at least one of its member signal types is
  // enabled. Toggling writes the agent's full signal_types set in or out
  // — never a subset — so partial states cannot be created from the UI.
  const isAgentEnabled = useCallback(
    (agent: RecommendationAgent) =>
      agent.signal_types.some((st) => enabledTypes.includes(st)),
    [enabledTypes]
  )

  const handleToggleAgent = useCallback(
    async (agent: RecommendationAgent) => {
      const currentlyEnabled = agent.signal_types.some((st) =>
        enabledTypes.includes(st)
      )
      const newTypes = currentlyEnabled
        ? enabledTypes.filter((t) => !agent.signal_types.includes(t))
        : Array.from(new Set([...enabledTypes, ...agent.signal_types]))
      setEnabledTypes(newTypes)
      setSavingField(`toggle_${agent.id}`)
      try {
        await onSaveEnabledSignalTypes(newTypes)
      } catch {
        toast.error('Failed to update agent setting')
        setEnabledTypes(initialEnabledSignalTypes)
      } finally {
        setSavingField(null)
      }
    },
    [enabledTypes, onSaveEnabledSignalTypes, initialEnabledSignalTypes]
  )

  const handleFilterLevelChange = useCallback(
    async (value: string) => {
      const previous = filterLevel
      setFilterLevel(value)
      setSavingField('filter_level')
      try {
        await onSaveFilterLevel(value)
        // Only remember a level that actually persisted — the master toggle
        // restores from this, so recording it optimistically would leave it
        // pointing at a value a failed save rolled back.
        if (value !== TURN_OFF) lastActiveLevelRef.current = value
      } catch {
        toast.error('Failed to update filter level')
        setFilterLevel(previous)
      } finally {
        setSavingField(null)
      }
    },
    [filterLevel, onSaveFilterLevel]
  )

  // Master switch (FIR-7884): off writes turn_off (kills the pipeline — no
  // daily summary, no LLM call); on restores the last real sensitivity level.
  const handleToggleAiEnabled = useCallback(
    (next: boolean) => {
      void handleFilterLevelChange(next ? lastActiveLevelRef.current : TURN_OFF)
    },
    [handleFilterLevelChange]
  )

  const enabledAgentCount = useMemo(
    () => agents.filter(isAgentEnabled).length,
    [agents, isAgentEnabled]
  )

  return (
    <div className="relative flex h-full min-h-0 w-full flex-1 flex-col bg-surface-page">
      <FBPageInnerTabsContainer>
        <UnderlineTabs
          tabs={TABS}
          activeKey={activeTab}
          onChange={setActiveTab}
          idPrefix={TAB_ID_PREFIX}
          aria-label="AI settings sections"
          className="w-full overflow-x-auto"
        />
      </FBPageInnerTabsContainer>

      {/* px-1/pt-1 keeps focus rings from being clipped — overflow-y-auto also
          clips the x-axis, so flush-left fields would lose their ring. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-1 pt-1">
        <div className="max-w-3xl pb-4">
          {/* TabPanel only renders its children when active (isActive &&
              children), so each panel below mounts lazily on selection. */}
          <TabPanel<AITab>
            idPrefix={TAB_ID_PREFIX}
            tabKey="overview"
            activeKey={activeTab}
          >
            <OverviewTab
              agents={agents}
              isAgentEnabled={isAgentEnabled}
              enabledAgentCount={enabledAgentCount}
              aiEnabled={aiEnabled}
              filterLevel={filterLevel}
              saving={savingField === 'filter_level'}
              hasFullInboxIntegration={hasFullInboxIntegration}
              onToggleAiEnabled={handleToggleAiEnabled}
              onNavigate={setActiveTab}
            />
          </TabPanel>

          <TabPanel<AITab>
            idPrefix={TAB_ID_PREFIX}
            tabKey="agents"
            activeKey={activeTab}
          >
            <AgentsTab
              agents={agents}
              aiEnabled={aiEnabled}
              enabledAgentCount={enabledAgentCount}
              isAgentEnabled={isAgentEnabled}
              savingField={savingField}
              hasFullInboxIntegration={hasFullInboxIntegration}
              onToggleAgent={handleToggleAgent}
            />
          </TabPanel>

          <TabPanel<AITab>
            idPrefix={TAB_ID_PREFIX}
            tabKey="personalization"
            activeKey={activeTab}
          >
            <PersonalizationTab
              isAdmin={isAdmin}
              initialCompanyContext={initialCompanyContext}
              savingField={savingField}
              setSavingField={setSavingField}
              onSaveCompanyContext={onSaveCompanyContext}
            />
          </TabPanel>

          <TabPanel<AITab>
            idPrefix={TAB_ID_PREFIX}
            tabKey="sensitivity"
            activeKey={activeTab}
          >
            <SensitivityTab
              filterLevel={filterLevel}
              aiEnabled={aiEnabled}
              saving={savingField === 'filter_level'}
              onChange={handleFilterLevelChange}
            />
          </TabPanel>

          <TabPanel<AITab>
            idPrefix={TAB_ID_PREFIX}
            tabKey="permissions"
            activeKey={activeTab}
          >
            <PermissionsTab
              emailIntegrations={emailIntegrations}
              savingField={savingField}
              setSavingField={setSavingField}
              onSaveInboxAccess={onSaveInboxAccess}
            />
          </TabPanel>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared section header
// ---------------------------------------------------------------------------

function SectionIntro({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  /** Optional right-aligned control on the title row (e.g. a CaretLink). */
  action?: React.ReactNode
}) {
  return (
    <div className="mb-6">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-xl font-bold text-fg-dark">{title}</h2>
        {action}
      </div>
      {description && (
        <p className="mt-1 max-w-2xl text-sm text-gray-500">{description}</p>
      )}
    </div>
  )
}

// Green text link with a trailing caret — used by the "Manage agents"
// affordance on the Overview tab.
function CaretLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded text-sm font-medium text-brand-active hover:text-basil-800',
        FOCUS_RING
      )}
    >
      {label}
      <PiCaretRight size={14} />
    </button>
  )
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({
  agents,
  isAgentEnabled,
  enabledAgentCount,
  aiEnabled,
  filterLevel,
  saving,
  hasFullInboxIntegration,
  onToggleAiEnabled,
  onNavigate,
}: {
  agents: RecommendationAgent[]
  isAgentEnabled: (agent: RecommendationAgent) => boolean
  enabledAgentCount: number
  aiEnabled: boolean
  filterLevel: string
  saving: boolean
  hasFullInboxIntegration: boolean
  onToggleAiEnabled: (next: boolean) => void
  onNavigate: (tab: AITab) => void
}) {
  const sensitivityLabel = aiEnabled
    ? (SENSITIVITY_LEVELS.find((l) => l.value === filterLevel)?.label ??
      'Normal')
    : 'Off'

  return (
    <div>
      <SectionIntro title="Overview" />

      {/* Global AI master switch (FIR-7884): off writes signal_filter_level
          = turn_off, which stops the daily summary and the recommendation
          pipeline entirely. */}
      <div
        className={cn(
          'flex items-center justify-between gap-4 rounded-xl border p-3.5 transition-colors',
          aiEnabled
            ? 'border-brand-active bg-success-bg-subtle'
            : 'border-line bg-surface'
        )}
      >
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-lg bg-brand-active text-white">
            <PiSparkle size={18} />
          </div>
          <div>
            <p className="text-sm font-semibold text-fg">AI Features</p>
            <p className="text-sm text-gray-500">
              {aiEnabled
                ? `${enabledAgentCount} agents running — recommendations active`
                : 'All AI features paused — no daily summary or recommendations'}
            </p>
          </div>
        </div>
        <Toggle
          size="md"
          checked={aiEnabled}
          disabled={saving}
          onChange={onToggleAiEnabled}
          aria-label="Toggle all AI features"
        />
      </div>

      {/* Agent status snapshot */}
      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-fg">Agent status</h3>
          <CaretLink
            label="Manage agents"
            onClick={() => onNavigate('agents')}
          />
        </div>
        <div className="divide-y divide-line rounded-xl border border-line">
          {agents.map((agent) => {
            const Icon = resolveAgentIcon(agent.icon)
            const enabled = aiEnabled && isAgentEnabled(agent)
            return (
              <div
                key={agent.id}
                className="flex items-center justify-between gap-3 px-4 py-3.5"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      'flex size-8 items-center justify-center rounded-lg',
                      AGENT_ICON_TILE
                    )}
                  >
                    <Icon size={16} />
                  </span>
                  <span className="text-sm font-medium text-fg">
                    {agent.label}
                  </span>
                </div>
                <StatusBadge color={enabled ? 'success' : 'neutral'}>
                  {enabled ? 'On' : 'Off'}
                </StatusBadge>
              </div>
            )
          })}
        </div>
      </div>

      {/* At a glance — read-only snapshot of current settings (the tab bar
          handles navigation, so these inform rather than re-navigate). */}
      <div className="mt-8">
        <h3 className="mb-3 text-base font-semibold text-fg">At a glance</h3>
        <div className="divide-y divide-line rounded-xl border border-line">
          <GlanceRow
            label="Recommendation sensitivity"
            value={sensitivityLabel}
          />
          <GlanceRow
            label="Data permissions"
            value={
              hasFullInboxIntegration
                ? 'Full inbox access on'
                : 'Full inbox access off'
            }
          />
        </div>
      </div>
    </div>
  )
}

function GlanceRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <span className="flex items-center gap-2.5">
        <PiGear size={16} className="text-fg-tertiary" />
        <span className="text-sm font-medium text-fg">{label}</span>
      </span>
      <span className="text-sm text-gray-500">{value}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// My Agents tab (real — wired to enabled_signal_types)
// ---------------------------------------------------------------------------

function AgentsTab({
  agents,
  aiEnabled,
  enabledAgentCount,
  isAgentEnabled,
  savingField,
  hasFullInboxIntegration,
  onToggleAgent,
}: {
  agents: RecommendationAgent[]
  aiEnabled: boolean
  enabledAgentCount: number
  isAgentEnabled: (agent: RecommendationAgent) => boolean
  savingField: string | null
  hasFullInboxIntegration: boolean
  onToggleAgent: (agent: RecommendationAgent) => void
}) {
  return (
    <div>
      <SectionIntro
        title="My Agents"
        description="Manage which agents are active."
      />

      {/* When AI Features are off nothing runs regardless of which agents are
          enabled here, so surface that instead of the "N active" banner —
          which would otherwise read as running while the pipeline is paused. */}
      {aiEnabled ? (
        <div className="mb-4 inline-flex items-center gap-2 rounded-lg bg-success-bg-subtle px-4 py-2.5 text-sm text-success-strong">
          <PiSparkle size={16} />
          <span>
            <span className="font-semibold">
              {enabledAgentCount} of {agents.length}
            </span>{' '}
            agents active
          </span>
        </div>
      ) : (
        <Alert
          color="warning"
          className="mb-4"
          description="AI Features are turned off, so none of these agents run. Turn them back on from the Overview tab — your selections below are kept for when you do."
        />
      )}

      <div className="space-y-3">
        {agents.map((agent) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            enabled={isAgentEnabled(agent)}
            saving={savingField === `toggle_${agent.id}`}
            needsFullInbox={
              FULL_INBOX_DEPENDENT_AGENT_IDS.has(agent.id) &&
              !hasFullInboxIntegration
            }
            onToggle={() => onToggleAgent(agent)}
          />
        ))}
      </div>
    </div>
  )
}

function AgentCard({
  agent,
  enabled,
  saving,
  needsFullInbox,
  onToggle,
}: {
  agent: RecommendationAgent
  enabled: boolean
  saving: boolean
  needsFullInbox: boolean
  onToggle: () => void
}) {
  const Icon = resolveAgentIcon(agent.icon)

  return (
    <div
      className={cn(
        'rounded-xl border bg-surface transition-opacity',
        enabled ? 'border-line' : 'border-line opacity-70'
      )}
    >
      <div className="flex items-center gap-3 p-4">
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-lg',
            AGENT_ICON_TILE
          )}
        >
          <Icon size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-fg">{agent.label}</span>
            {needsFullInbox && (
              <StatusBadge color="neutral" icon={PiLock}>
                Limited without full inbox
              </StatusBadge>
            )}
          </div>
          <p className="mt-0.5 text-sm text-gray-500">{agent.description}</p>
        </div>
        <Toggle
          checked={enabled}
          disabled={saving}
          onChange={onToggle}
          aria-label={`Toggle ${agent.label}`}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Personalization tab
//
// Per-user recommendation notes (real — UserRecommendationNote) grouped by the
// three backend categories, plus the company-wide Company Context (real
// admin-owned llm_context). Note categories mirror
// server/recommendations/models.py::UserRecommendationNoteCategory.
// ---------------------------------------------------------------------------

const PERSONALIZATION_SUBTAB_ID = 'personalization'

// key = the backend UserRecommendationNoteCategory value; title is the label
// shown in the UI. Keep the keys in sync with the server enum.
const NOTE_SUBTABS: ReadonlyArray<{
  key: string
  title: string
  placeholder: string
}> = [
  {
    key: 'work_role',
    title: 'Your Role',
    placeholder:
      'e.g. "I am a senior account executive focused on enterprise foodservice accounts"',
  },
  {
    key: 'message_tone',
    title: 'Tone',
    placeholder: 'e.g. "Keep outreach warm but concise. Never use jargon."',
  },
  {
    key: 'global',
    title: 'Misc',
    placeholder: 'Anything else the AI should always keep in mind',
  },
]

function PersonalizationTab({
  isAdmin,
  initialCompanyContext,
  savingField,
  setSavingField,
  onSaveCompanyContext,
}: {
  isAdmin: boolean
  initialCompanyContext: string
  savingField: string | null
  setSavingField: (field: string | null) => void
  onSaveCompanyContext: (companyContext: string) => Promise<void>
}) {
  const api = useApiService()
  const [subTab, setSubTab] = useState<string>(NOTE_SUBTABS[0].key)
  const [companyContext, setCompanyContext] = useState(initialCompanyContext)
  const savedCompanyContextRef = useRef(initialCompanyContext)

  const { data: notes, isLoading: isLoadingNotes } =
    api.useGetRecommendationNotes()
  const addNote = api.useAddRecommendationNote()
  const deleteNote = api.useDeleteRecommendationNote()

  const [draft, setDraft] = useState('')
  // Track in-flight deletes per note id so one delete doesn't disable every
  // other card's remove button (deleteNote is a single shared mutation).
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set())

  const allNotes = notes ?? []
  const activeSubTab =
    NOTE_SUBTABS.find((t) => t.key === subTab) ?? NOTE_SUBTABS[0]
  const visibleNotes = allNotes.filter((n) => n.category === subTab)
  // The 10-note cap is across all categories (bounds the synthesis prompt).
  const atCap = allNotes.length >= MAX_NOTES
  const canAdd = draft.trim().length > 0 && !atCap && !addNote.isPending

  const subTabs = NOTE_SUBTABS.map((t) => ({ key: t.key, title: t.title }))

  const handleAdd = useCallback(async () => {
    const content = draft.trim()
    if (!content || atCap || addNote.isPending) return
    try {
      await addNote.mutateAsync({ content, category: subTab })
      setDraft('')
    } catch {
      toast.error('Failed to add note')
    }
  }, [draft, atCap, addNote, subTab])

  const handleDelete = useCallback(
    async (id: number) => {
      setDeletingIds((prev) => new Set(prev).add(id))
      try {
        await deleteNote.mutateAsync(id)
      } catch {
        toast.error('Failed to remove note')
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      }
    },
    [deleteNote]
  )

  const handleCompanyContextBlur = useCallback(async () => {
    if (companyContext === savedCompanyContextRef.current) return
    setSavingField('company_context')
    try {
      await onSaveCompanyContext(companyContext)
      savedCompanyContextRef.current = companyContext
    } catch {
      toast.error('Failed to save company context')
    } finally {
      setSavingField(null)
    }
  }, [companyContext, onSaveCompanyContext, setSavingField])

  return (
    <div>
      <SectionIntro
        title="Personalization"
        description="Everything the AI should know about you and how it should sound — your role, your preferred tone, and anything else worth keeping in mind. These apply only to you, with the exception of Company Context (shared across your workspace and configured by an admin)."
      />

      <PillTabs
        tabs={subTabs}
        activeKey={subTab}
        onChange={(key) => {
          setSubTab(key)
          setDraft('')
        }}
        idPrefix={PERSONALIZATION_SUBTAB_ID}
        aria-label="Personalization sections"
        fullWidth
        className="mb-4"
      />

      <TabPanel
        idPrefix={PERSONALIZATION_SUBTAB_ID}
        tabKey={subTab}
        activeKey={subTab}
      >
        <NotesField
          notes={visibleNotes}
          draft={draft}
          onDraftChange={setDraft}
          onAdd={handleAdd}
          canAdd={canAdd}
          adding={addNote.isPending}
          onRemove={handleDelete}
          deletingIds={deletingIds}
          placeholder={activeSubTab.placeholder}
          loading={isLoadingNotes}
          atCap={atCap}
        />
      </TabPanel>

      {/* Company Context — workspace-wide + admin-owned, so it sits apart from
          the per-user notes above. Real: saves company `llm_context`. */}
      <section className="mt-8 border-t border-line pt-8">
        <div className="mb-1 flex items-center gap-2">
          <h3 className="text-base font-semibold text-fg">Company Context</h3>
          <StatusBadge color="neutral" icon={PiLock}>
            Shared · Admin
          </StatusBadge>
        </div>
        {isAdmin ? (
          <>
            <p className="mb-3 text-sm text-gray-500">
              Shared across everyone at your company. Describe your products,
              target market, distributors, and typical sales cycle so
              recommendations are grounded in your business.
            </p>
            <textarea
              value={companyContext}
              aria-label="Company context"
              onChange={(e) => {
                if (e.target.value.length <= TEXT_MAX_LENGTH) {
                  setCompanyContext(e.target.value)
                }
              }}
              onBlur={handleCompanyContextBlur}
              placeholder={COMPANY_CONTEXT_PLACEHOLDER}
              rows={5}
              className="w-full resize-y rounded-lg border border-line bg-surface p-3 text-sm text-fg placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
            <div className="mt-1 flex justify-between">
              <p className="text-xs text-fg-tertiary">
                {savingField === 'company_context'
                  ? 'Saving...'
                  : 'Applies to all users'}
              </p>
              <p className="text-xs text-fg-tertiary">
                {companyContext.length}/{TEXT_MAX_LENGTH}
              </p>
            </div>
          </>
        ) : (
          <>
            <p className="mb-3 text-sm text-gray-500">
              Shared across everyone at your company and used to ground your
              recommendations. Set by your workspace admin — you can view it but
              not edit it.
            </p>
            {companyContext.trim() ? (
              <div className="whitespace-pre-wrap rounded-lg border border-line bg-neutral-100 p-3 text-sm text-fg">
                {companyContext}
              </div>
            ) : (
              <div className="rounded-lg border-2 border-dotted border-gray-300 py-10 text-center text-sm text-gray-500">
                Your admin hasn&apos;t added company context yet.
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}

// Per-user note list for one category: add via the input, remove via the
// trash affordance. Backend supports add + delete only (no in-place edit), so
// existing notes render read-only.
function NotesField({
  notes,
  draft,
  onDraftChange,
  onAdd,
  canAdd,
  adding,
  onRemove,
  deletingIds,
  placeholder,
  loading,
  atCap,
}: {
  notes: UserRecommendationNote[]
  draft: string
  onDraftChange: (v: string) => void
  onAdd: () => void
  canAdd: boolean
  adding: boolean
  onRemove: (id: number) => void
  deletingIds: Set<number>
  placeholder: string
  loading: boolean
  atCap: boolean
}) {
  // Which note row is hovered/focused — drives the reveal of its trash button.
  const [activeId, setActiveId] = useState<number | null>(null)

  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          maxLength={MAX_NOTE_LENGTH}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onAdd()
            }
          }}
          placeholder={placeholder}
          disabled={atCap}
          aria-label="Add a note"
          className="flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-fg placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-neutral-100"
        />
        <Ds2026ButtonProvider>
          <FbButton
            type="button"
            variant="primary"
            onClick={onAdd}
            disabled={!canAdd}
            loading={adding}
          >
            Add
          </FbButton>
        </Ds2026ButtonProvider>
      </div>
      {atCap && (
        <p className="mt-1.5 text-xs text-fg-tertiary">
          Limit of {MAX_NOTES} notes reached (across all categories). Remove one
          to add another.
        </p>
      )}

      {loading ? (
        <p className="mt-3 text-sm text-gray-500">Loading…</p>
      ) : notes.length > 0 ? (
        <div className="mt-3 space-y-2">
          {notes.map((note) => (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              key={note.id}
              onMouseEnter={() => setActiveId(note.id)}
              onMouseLeave={() =>
                setActiveId((cur) => (cur === note.id ? null : cur))
              }
              onFocus={() => setActiveId(note.id)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setActiveId((cur) => (cur === note.id ? null : cur))
                }
              }}
              className="flex items-start justify-between gap-2 rounded-lg border border-line bg-surface px-3 py-2"
            >
              <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-sm text-fg">
                {note.content}
              </span>
              <button
                type="button"
                aria-label="Remove note"
                onClick={() => onRemove(note.id)}
                disabled={deletingIds.has(note.id)}
                className={cn(
                  'shrink-0 rounded p-1 text-fg-tertiary transition-opacity hover:bg-neutral-200 hover:text-fg-secondary disabled:opacity-50',
                  activeId === note.id ? 'opacity-100' : 'opacity-0'
                )}
              >
                <PiTrash size={16} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-3 rounded-lg border-2 border-dotted border-gray-300 py-8 text-center text-sm text-gray-500">
          {adding ? 'Adding…' : 'No notes in this category yet. Add one above.'}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Recommendation Sensitivity tab (real — wired to signal_filter_level)
// ---------------------------------------------------------------------------

function SensitivityTab({
  filterLevel,
  aiEnabled,
  saving,
  onChange,
}: {
  filterLevel: string
  aiEnabled: boolean
  saving: boolean
  onChange: (value: string) => void
}) {
  return (
    <div>
      <SectionIntro
        title="Recommendation Sensitivity"
        description="Control how many recommendations surface in your daily summary. Conservative shows only the highest-confidence signals; Aggressive casts a wider net."
      />

      {/* When AI Features are off (turn_off) no card reads as selected, so a
          notice explains why and where to turn it back on. Picking any level
          here turns the feature back on. */}
      {!aiEnabled && (
        <Alert
          color="warning"
          className="mb-4"
          description="AI Features are turned off, so no recommendations are generated. Turn them back on from the Overview tab — or pick a sensitivity below to re-enable them."
        />
      )}

      <div
        className={cn(
          'space-y-2.5',
          saving && 'pointer-events-none opacity-60'
        )}
      >
        {SENSITIVITY_LEVELS.map((level) => (
          <SelectableCard
            key={level.value}
            selected={aiEnabled && filterLevel === level.value}
            disabled={saving}
            className="min-h-20"
            onClick={() => onChange(level.value)}
            title={level.label}
            description={level.description}
            badge={
              level.value === 'normal' ? (
                <StatusBadge color="success">Recommended</StatusBadge>
              ) : undefined
            }
          />
        ))}
      </div>
    </div>
  )
}

function SelectableCard({
  selected,
  onClick,
  title,
  description,
  badge,
  disabled,
  className,
}: {
  selected: boolean
  onClick: () => void
  title: string
  description: string
  badge?: React.ReactNode
  disabled?: boolean
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={title}
      className={cn(
        'flex w-full items-start justify-between gap-3 rounded-lg border p-4 text-left transition-colors disabled:cursor-default',
        FOCUS_RING,
        selected
          ? 'border-brand-active bg-success-bg-subtle'
          : 'border-line hover:bg-surface-hover',
        className
      )}
    >
      <span>
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold text-fg">{title}</span>
          {badge}
        </span>
        <span className="mt-0.5 block text-sm text-gray-500">
          {description}
        </span>
      </span>
      <span
        className={cn(
          'mt-0.5 size-4 shrink-0 rounded-full border transition-colors',
          selected
            ? 'border-brand-active bg-brand-active'
            : 'border-gray-300 bg-white'
        )}
      />
    </button>
  )
}

// ---------------------------------------------------------------------------
// Permissions tab (real — per-integration full inbox access)
// ---------------------------------------------------------------------------

function PermissionsTab({
  emailIntegrations,
  savingField,
  setSavingField,
  onSaveInboxAccess,
}: {
  emailIntegrations: EmailIntegration[]
  savingField: string | null
  setSavingField: (field: string | null) => void
  onSaveInboxAccess: (
    integrationId: number,
    inboxAccess: EmailIntegration['inbox_access']
  ) => Promise<void>
}) {
  const handleToggle = useCallback(
    async (integration: EmailIntegration) => {
      const next: EmailIntegration['inbox_access'] =
        integration.inbox_access === 'full_inbox_access'
          ? 'contact_scoped_access'
          : 'full_inbox_access'
      setSavingField(`inbox_access_${integration.id}`)
      try {
        await onSaveInboxAccess(integration.id, next)
      } catch {
        toast.error('Failed to update full inbox access')
      } finally {
        setSavingField(null)
      }
    },
    [onSaveInboxAccess, setSavingField]
  )

  return (
    <div>
      <SectionIntro
        title="Permissions"
        description="Manage what data AI Agents can access. Some agents require additional permissions to function."
      />

      {emailIntegrations.length === 0 ? (
        <p className="text-sm text-fg-tertiary italic">
          Connect an email to manage permissions.
        </p>
      ) : (
        <div className="space-y-3">
          {emailIntegrations.map((integration) => (
            <PermissionRow
              key={integration.id}
              icon={PiEnvelopeSimple}
              title="Full inbox access"
              description="Allows AI Agents to read email from senders not yet in your CRM. Required for the 'create new deal' half of Deal Updates and the 'create new contact' half of Contact Updates. Stage-change and contact-update suggestions still work without it."
              subLabel={
                emailIntegrations.length > 1 ? integration.email : undefined
              }
              checked={integration.inbox_access === 'full_inbox_access'}
              disabled={savingField === `inbox_access_${integration.id}`}
              onToggle={() => handleToggle(integration)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function PermissionRow({
  icon: Icon,
  title,
  description,
  subLabel,
  checked,
  disabled,
  onToggle,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>
  title: string
  description: string
  subLabel?: string
  checked: boolean
  disabled?: boolean
  onToggle: () => void
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 rounded-xl border p-4 transition-colors',
        checked
          ? 'border-brand-active bg-success-bg-subtle'
          : 'border-line bg-surface'
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-lg',
            checked
              ? 'bg-brand-active text-white'
              : 'bg-neutral-100 text-fg-tertiary'
          )}
        >
          <Icon size={18} />
        </span>
        <div>
          <p className="text-sm font-semibold text-fg">{title}</p>
          <p className="mt-1 max-w-xl text-sm leading-relaxed text-gray-500">
            {description}
          </p>
          {subLabel && (
            <p className="mt-1 text-xs text-fg-tertiary">{subLabel}</p>
          )}
        </div>
      </div>
      <Toggle
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        aria-label={`Toggle ${title}`}
      />
    </div>
  )
}
